// Story 6.27 (ADO-1206) — deep-link return after sign-in.
//
// All the react-router coupling for restoring a user's intended route after Auth0
// Universal Login lives HERE, kept out of `App` (which stays router-hook-free so
// it renders standalone in unit tests) and out of `main.tsx` (whose module-load
// `createRoot` side effect makes it un-importable in a test). Both pieces are the
// consumers of the `appState.returnTo` seam threaded through `auth0-client`
// (`signIn` → `completeRedirectCallback`) and the `useAuthStore.postLoginReturnTo`
// hand-off the detached bootstrap uses (ADR-004 — Zustand for global state).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { isSafeReturnTo } from '../auth/auth0-client';
import { useAuthStore } from '../auth/useAuthStore';
import { useSessionStore } from '../auth/useSessionStore';
import { postSignInDestination } from '../ordering/orderingSession';
import { ActivationLinkExpired } from './ActivationLinkExpired';

/**
 * Session-scoped breaker for the automatic sign-in start on `/`.
 *
 * Code review 2026-07-31: auto-login replaced a button the user had to click, and
 * `signInStarted` below is a `useRef` — it resets on every new document, so it
 * cannot stop a loop that runs THROUGH a full-page redirect. If the boot token
 * probe fails transiently the bootstrap settles signed-out, `/` redirects to
 * Auth0, a live SSO session sends the user straight back, the probe fails again,
 * and it redirects again, indefinitely. `sessionStorage` survives the redirect and
 * gives us one automatic attempt per tab session; the second lands on the existing
 * retry surface instead, where the user is in control.
 */
const AUTO_SIGN_IN_ATTEMPT_KEY = 'shoplink.autoSignInAttempted';

function autoSignInAlreadyAttempted(): boolean {
  try {
    return window.sessionStorage.getItem(AUTO_SIGN_IN_ATTEMPT_KEY) === '1';
  } catch {
    // Private-mode / storage-disabled: fall back to allowing the attempt. The
    // per-page-load ref still prevents an in-page loop.
    return false;
  }
}

function markAutoSignInAttempted(): void {
  try {
    window.sessionStorage.setItem(AUTO_SIGN_IN_ATTEMPT_KEY, '1');
  } catch {
    // Storage unavailable — nothing to record; see above.
  }
}

/**
 * Clear the breaker once a sign-in has demonstrably succeeded, so a later
 * sign-out in the same tab still gets its one automatic attempt.
 */
function clearAutoSignInAttempt(): void {
  try {
    window.sessionStorage.removeItem(AUTO_SIGN_IN_ATTEMPT_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

/**
 * The shared "bounce an unauthenticated deep-link to the sign-in surface"
 * redirect. Replaces the bare `<Navigate to="/" replace />` in every protected-
 * route guard: it captures the route the user actually asked for (pathname +
 * search + hash) in react-router history `state` so the `/`-route {@link AppRoute}
 * can thread it into sign-in as `appState.returnTo` and return them there after
 * login — instead of the intended route being lost and every deep-link landing on
 * the origin. Defined once so the six guards stay in lock-step.
 *
 * HONEST LIMIT (story D-2 / OQ-6.27a): history `state` does not survive a hard
 * reload of "/", so a reload before signing in falls back to the origin —
 * acceptable for the minimal flow the ticket scopes.
 */
export function RedirectToSignIn() {
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  return <Navigate to="/" replace state={{ returnTo }} />;
}

/**
 * The `/` route wrapper auto-starts Universal Login for signed-out users and
 * chooses exactly one destination for authenticated users. The destination is
 * latched before `postLoginReturnTo` is consumed so the resulting store re-render
 * cannot overwrite a recovered deep link with the default route.
 *
 * Story 8.1 (ADO-2010), Task 5.5: the default is no longer a fixed `/products`. The
 * ordering choices resolve inside the bootstrap, before it reports `ready`, so at the
 * latch `postSignInDestination` already knows whether the Shopper was taken straight
 * in (their account's landing page or the product list) or must choose (`/choose`).
 * A deep link (`postLoginReturnTo`) still wins.
 */
export function AppRoute() {
  const location = useLocation();
  const status = useAuthStore((s) => s.status);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const postLoginReturnTo = useAuthStore((s) => s.postLoginReturnTo);
  const consumeReturnTo = useAuthStore((s) => s.consumeReturnTo);
  const signIn = useAuthStore((s) => s.signIn);
  const requestActivationLink = useAuthStore((s) => s.requestActivationLink);
  const signInStarted = useRef(false);
  const authenticatedDestination = useRef<string | null>(null);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [autoStartExhausted, setAutoStartExhausted] = useState(false);

  if (authenticatedDestination.current === null && status === 'ready' && isAuthenticated) {
    authenticatedDestination.current =
      postLoginReturnTo ?? postSignInDestination(useSessionStore.getState());
  }

  useEffect(() => {
    if (status === 'ready' && isAuthenticated && postLoginReturnTo !== null) {
      consumeReturnTo();
    }
  }, [status, isAuthenticated, postLoginReturnTo, consumeReturnTo]);

  const returnToFromState = (location.state as { returnTo?: unknown } | null)?.returnTo;
  const safeReturnTo = isSafeReturnTo(returnToFromState) ? returnToFromState : undefined;

  const startSignIn = useCallback(
    (auto: boolean) => {
      if (signIn === null || signInStarted.current) return;
      // Automatic starts are rationed across full-page redirects; an explicit
      // "Retry sign in" click always proceeds and resets the ration.
      if (auto) {
        if (autoSignInAlreadyAttempted()) {
          setAutoStartExhausted(true);
          return;
        }
        markAutoSignInAttempted();
      } else {
        clearAutoSignInAttempt();
        setAutoStartExhausted(false);
      }
      signInStarted.current = true;
      setSignInError(null);
      try {
        void signIn(safeReturnTo).catch((error: unknown) => {
          signInStarted.current = false;
          setSignInError(error instanceof Error ? error.message : 'Sign-in failed');
        });
      } catch (error: unknown) {
        signInStarted.current = false;
        setSignInError(error instanceof Error ? error.message : 'Sign-in failed');
      }
    },
    [signIn, safeReturnTo],
  );

  const retrySignIn = useCallback(() => startSignIn(false), [startSignIn]);

  useEffect(() => {
    if (status === 'ready' && !isAuthenticated) startSignIn(true);
  }, [status, isAuthenticated, startSignIn]);

  // A completed sign-in clears the breaker so a later sign-out in the same tab
  // still gets its one automatic attempt.
  useEffect(() => {
    if (status === 'ready' && isAuthenticated) clearAutoSignInAttempt();
  }, [status, isAuthenticated]);

  if (status === 'initializing') {
    return <p role="status">Loading…</p>;
  }

  if (status === 'ready' && isAuthenticated) {
    return (
      <Navigate
        to={authenticatedDestination.current ?? postSignInDestination(useSessionStore.getState())}
        replace
      />
    );
  }

  if (status === 'unconfigured') {
    return <p role="alert">Authentication is not configured.</p>;
  }

  // Story 6.4 (ADO-880, AC3) — an EXPIRED / already-used set-password link gets the
  // friendly recovery surface with an enumeration-safe re-request action, NOT the
  // generic failure message. Code review 2026-07-31: retiring the logged-out shell
  // collapsed this into the generic `error` branch, which silently dropped a
  // shipped story surface (and left `requestActivationLink` unreachable). D-6 only
  // records losing the in-app PASSWORD-RESET panel; this one was never in scope.
  if (status === 'activation-error') {
    return (
      <ActivationLinkExpired
        onRequestNewLink={requestActivationLink}
        // Re-run the bootstrap from the (already error-param-stripped) URL — an
        // in-tab way out of the dead end without a manual reload.
        onReturnToSignIn={() => window.location.reload()}
      />
    );
  }

  if (status === 'error') {
    return <p role="alert">Authentication could not be completed.</p>;
  }

  if (signInError !== null || autoStartExhausted) {
    return (
      <main>
        <p role="alert">
          {signInError !== null
            ? `Sign-in could not be started. Please try again. (${signInError})`
            : 'Sign-in did not complete. Please try again.'}
        </p>
        <button type="button" onClick={retrySignIn}>
          Retry sign in
        </button>
      </main>
    );
  }

  return <p role="status">Redirecting to sign in…</p>;
}
